import { useState } from "react";
import { useTranslation } from "react-i18next";
import { leads } from "@/lib/api";
import { CheckCircle2, ArrowRight } from "lucide-react";

const LeadCapture = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", interest: "Trading Pro" });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await leads.create(form);
      setDone(true);
    } catch {
      setError("Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="lead" data-testid="lead-section" className="py-24 md:py-32 border-b border-zinc-200 bg-zinc-950 text-white relative overflow-hidden">
      <div className="absolute inset-0 grain pointer-events-none" />
      <div className="max-w-5xl mx-auto px-6 md:px-12 relative">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF3B30] mb-4">{t("lead.tag")}</p>
            <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter leading-[0.95]">
              {t("lead.title1")}<br />
              {t("lead.title2")} <span className="text-[#4F46E5]">{t("lead.title3")}</span>
            </h2>
            <p className="mt-6 text-zinc-400 text-lg max-w-md">{t("lead.subtitle")}</p>
          </div>

          <div className="md:col-span-5">
            {done ? (
              <div data-testid="lead-success" className="bg-white text-zinc-900 p-8 flex flex-col items-center text-center">
                <CheckCircle2 className="w-12 h-12 text-[#4F46E5] mb-4" />
                <h3 className="font-display font-bold text-xl mb-2">{t("lead.successTitle")} {form.name}</h3>
                <p className="text-zinc-600 text-sm">{t("lead.successDesc")}</p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="lead-form" className="bg-white text-zinc-900 p-8 space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-wider font-bold mb-2 block">{t("lead.name")}</label>
                  <input
                    required
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full border border-zinc-300 px-4 py-3 focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5] outline-none"
                    placeholder={t("lead.namePh")}
                    data-testid="lead-input-name"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider font-bold mb-2 block">{t("lead.email")}</label>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full border border-zinc-300 px-4 py-3 focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5] outline-none"
                    placeholder={t("lead.emailPh")}
                    data-testid="lead-input-email"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider font-bold mb-2 block">{t("lead.interest")}</label>
                  <select
                    value={form.interest}
                    onChange={(e) => setForm({ ...form, interest: e.target.value })}
                    className="w-full border border-zinc-300 px-4 py-3 focus:border-[#4F46E5] outline-none bg-white"
                    data-testid="lead-input-interest"
                  >
                    <option>Trading Pro</option>
                    <option>E-commerce LATAM</option>
                    <option>IA para Emprendedores</option>
                    <option>Finanzas Personales</option>
                    <option>—</option>
                  </select>
                </div>
                {error && <p className="text-sm text-red-600">{error}</p>}
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="lead-submit-btn"
                  className="group w-full inline-flex items-center justify-center gap-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white px-6 py-4 font-semibold transition-colors disabled:opacity-50"
                >
                  {loading ? t("lead.submitting") : t("lead.submit")}
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </button>
                <p className="text-xs text-zinc-500 text-center">{t("lead.noSpam")}</p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default LeadCapture;
